import { useRef, useState } from 'react';
import { DndContext, KeyboardSensor, PointerSensor, closestCenter, useSensor, useSensors, type DragEndEvent } from '@dnd-kit/core';
import { SortableContext, arrayMove, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { FileIcon, GripVertical, Maximize2, Play, Plus } from 'lucide-react';
import type { TaskAttachment, TaskDetail } from '@shared/types/tasks';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { reorderTaskAttachments } from '@/lib/tasks-api';
import { optimisticUpdate } from '@/stores/taskOptimistic';
import { cn } from '@/lib/utils';
import { formatBytes, formatDateTime } from '@/lib/formatters';
import { attachFilesToTask, isPlayableVideo } from './task-files';
import { useAttachmentLink } from './task-attachment-link';
import { Spinner } from '@/components/ui/spinner';

/** A thumbnail, or a file icon for other files and for an image whose link has expired. */
function Thumb({ a }: { a: TaskAttachment }) {
  const [failed, setFailed] = useState(false);
  if (isPlayableVideo(a) && !failed) {
    // The first frame, with a play mark over it.
    return (
      <span className="relative h-12 w-16 shrink-0">
        <video
          src={`${a.url}#t=0.1`}
          muted
          playsInline
          preload="metadata"
          className="h-12 w-16 rounded border bg-muted object-cover"
          onError={() => setFailed(true)}
        />
        <Play className="absolute inset-0 m-auto size-5 rounded-full bg-black/60 p-1 fill-white text-white" />
      </span>
    );
  }
  if (!a.is_image || failed) {
    return (
      <span className="flex h-12 w-16 shrink-0 items-center justify-center rounded border bg-muted">
        <FileIcon className="size-5 text-muted-foreground" />
      </span>
    );
  }
  return (
    <img
      src={a.url}
      alt=""
      loading="lazy"
      className="h-12 w-16 shrink-0 rounded border bg-muted object-cover"
      onError={() => setFailed(true)}
    />
  );
}

/** Files that can be a task's cover: images and videos (T-331). */
function canBeCover(a: Pick<TaskAttachment, 'content_type'>): boolean {
  return a.content_type.startsWith('image/') || a.content_type.startsWith('video/');
}

/**
 * Every file on the task, whichever message it came with (like Trello's Attachments), in the
 * task's own order: drag one to the top to make it the cover (T-331), the first image or video.
 * New files go at the bottom. "Add" posts the files as a chat message, as dropping them does.
 */
export function TaskAttachments({ task }: { task: TaskDetail }) {
  const input = useRef<HTMLInputElement>(null);
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );
  // The saved order, then any file just sent from a message (still uploading, or not reloaded yet).
  const ordered = new Set(task.attachments.map((a) => a.id));
  const files = [...task.attachments, ...task.events.flatMap((e) => e.attachments).filter((a) => !ordered.has(a.id))];
  if (!files.length) return null;
  const cover = files.find(canBeCover);
  const saved = files.filter((a) => !a.id.startsWith('local-'));

  const onDragEnd = ({ active, over }: DragEndEvent) => {
    if (!over || active.id === over.id) return;
    const from = saved.findIndex((a) => a.id === active.id);
    const to = saved.findIndex((a) => a.id === over.id);
    if (from < 0 || to < 0) return;
    const next = arrayMove(saved, from, to);
    void optimisticUpdate(task.id, { fields: { attachments: next } }, reorderTaskAttachments(task.id, next.map((a) => a.id)));
  };

  return (
    <section className="space-y-2">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold">
          Attachments <span className="font-normal text-muted-foreground">{files.length}</span>
        </h3>
        <Button variant="outline" size="compact" onClick={() => input.current?.click()}>
          <Plus className="size-3.5" />
          Add
        </Button>
        <input
          ref={input}
          type="file"
          multiple
          hidden
          onChange={(e) => {
            const picked = [...(e.target.files ?? [])];
            e.target.value = '';
            if (picked.length) void attachFilesToTask(task.id, picked);
          }}
        />
      </div>
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
        <SortableContext items={saved.map((a) => a.id)} strategy={verticalListSortingStrategy}>
          <ul className="space-y-1.5">
            {files.map((a) => (
              <FileRow key={a.id} a={a} cover={a.id === cover?.id} sortable={saved.length > 1 && !a.id.startsWith('local-')} />
            ))}
          </ul>
        </SortableContext>
      </DndContext>
    </section>
  );
}

/** One file, with a handle to drag it; a click opens it in the task's viewer. */
function FileRow({ a, cover, sortable }: { a: TaskAttachment; cover: boolean; sortable: boolean }) {
  const link = useAttachmentLink(a);
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: a.id, disabled: !sortable });
  return (
    <li
      ref={setNodeRef}
      style={{ transform: CSS.Translate.toString(transform), transition }}
      className={cn('flex items-center gap-1', isDragging && 'relative z-10 rounded-md bg-background shadow-md')}
    >
      {sortable ? (
        <button
          type="button"
          aria-label={`Move ${a.filename}`}
          data-hint="Drag to reorder: the top picture or video is the cover"
          className="flex h-12 w-5 shrink-0 cursor-grab touch-none items-center justify-center rounded text-muted-foreground hover:bg-muted hover:text-foreground active:cursor-grabbing"
          {...attributes}
          {...listeners}
        >
          <GripVertical className="size-4" />
        </button>
      ) : null}
      <FileLink a={a} link={link} cover={cover} />
    </li>
  );
}

function FileLink({ a, link, cover }: { a: TaskAttachment; link: ReturnType<typeof useAttachmentLink>; cover: boolean }) {
  return (
    <a {...link} className="group flex min-w-0 flex-1 items-center gap-3 rounded-md p-1 hover:bg-muted/60">
      <Thumb a={a} />
      <span className="min-w-0 flex-1">
        <span className="flex min-w-0 items-center gap-1.5">
          <span className="truncate text-sm font-medium">{a.filename}</span>
          {cover ? (
            <Badge variant="info" shape="square" size="sm" className="shrink-0" data-hint="The task’s cover: the top picture or video">
              Cover
            </Badge>
          ) : null}
        </span>
        {/* A file just pasted or dropped is shown from this computer until its upload lands. */}
        {a.id.startsWith('local-') ? (
          <span className="flex items-center gap-1 text-xs text-primary">
            <Spinner size="xs" />
            Uploading… · {formatBytes(a.size)}
          </span>
        ) : (
          <span className="block text-xs text-muted-foreground">
            Added {formatDateTime(new Date(a.created_at))} · {formatBytes(a.size)}
          </span>
        )}
      </span>
      <Maximize2 className="size-4 shrink-0 text-muted-foreground opacity-0 group-hover:opacity-100" />
    </a>
  );
}
